import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import {
    PropertiesPanelAccordion,
    PropertiesPanelColorProperty,
    PropertiesPanelRangeProperty,
    PropertiesPanelResourceProperty,
    PropertiesPanelSection,
    PropertiesPanelSwitchProperty
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { EnvironmentsIcon } from '../../../../../widgets/icons/scene';
import { useEnvironmentTextures } from './hooks/useEnvironmentTextures';
import InspectorShell from './inspectorShell';
import { fileNameOf } from './sceneUrls';

// WORLD — ciò che sta INTORNO al modello: l'ambiente che lo illumina, lo sfondo su cui si
// staglia, il piano su cui poggia. Sono tre cose che nel backoffice vivevano sparse — la
// texture d'ambiente nelle impostazioni, il ground in un flag del motore, lo sfondo da
// nessuna parte — e qui tornano una schermata sola.
//
// Ogni riga scrive sul motore E sul config della scena: il motore per vederlo subito, il
// config perché è quello che il salvataggio manda al server. Scrivere solo sul motore
// significa una modifica che sparisce ricaricando.

// LA TESTATA, I CAPITOLI E LO SLOT NON SI DISEGNANO PIÙ QUI. Erano scritti in questo file — e
// un'altra volta in `effectsEditor`, e un'altra ancora nei pannelli degli oggetti — e a forza di
// riscriverli le copie hanno smesso di assomigliarsi. Ora la testata è `InspectorShell`, il
// capitolo è `PropertiesPanelSection`, lo slot di un asset è `PropertiesPanelResourceProperty`:
// chi li cambia li cambia una volta, per tutti.

const Note = styled.p`
    margin: 6px 0 0;
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
`;

// L'AMBIENTE DI FABBRICA SI RICONOSCE DAL NOME DEL FILE, e quel nome sta scritto qui invece di
// essere importato dal core. `DefaultAssetUrls` vive in `dist/index.js`, che si aspetta `BABYLON`
// già sulla finestra: quei globali arrivano da uno script a parte, e importare il core da un
// modulo di pannello lo faceva valutare prima che ci fossero — «BABYLON is not defined», e la
// pagina non si apriva. Nel core la costante per esteso è `DefaultAssetUrls.Environment
// .EnvironmentTextureName`; qui basta il prefisso, che sopravvive anche a un cambio di quel file.
const DEFAULT_ENVIRONMENT_NAME = 'environmentSpecular_';

interface WorldEditorProps {
    sceneEditor?: any;
    zkScene?: any;
    additionalFeatures?: any;
    /** Caricare o togliere una texture è un gesto da pulsante, e i pulsanti non emettono `change`. */
    onSceneChanged?: () => void;
}

const WorldEditor: FC<WorldEditorProps> = ({ sceneEditor, zkScene, additionalFeatures, onSceneChanged }) => {
    const generalConfig = zkScene?.getOrCreateGeneralConfig?.();
    const environmentConfig = zkScene?.getOrCreateEnvironmentConfig?.();
    // LO SKYBOX SI LEGGE SENZA CREARLO: con `getOrCreateSkyboxConfig` bastava APRIRE questo
    // pannello perché una scena senza fondale si portasse via un blocco `skybox` a metà —
    // `createSkybox` ce l'ha di default, `blur` no — e `SceneSkyboxConfigInput.blur` è `Float!`,
    // quindi a fallire sarebbe stato il salvataggio dell'intera scena.
    //
    // Non chiude il buco per intero, e il resto non sta qui: i setter del motore scrivono un campo
    // alla volta, quindi accendere il fondale su una scena che non l'ha mai avuto lascia lo stesso
    // un blocco senza `blur`. Il posto dove si ripara è il modello — `Zakeke.SceneSkyboxConfig`,
    // che un default per `createSkybox` ce l'ha già e per `blur` no.
    const skyboxConfig = zkScene?.getSkyboxConfig?.();

    // Caricare e togliere le due texture d'ambiente è la stessa procedura per tutti e due, e
    // ora sta in un posto solo: prima viveva dentro le Impostazioni, ed è il motivo per cui
    // qui la mappa si poteva vedere ma non sostituire.
    const environment = useEnvironmentTextures(sceneEditor, additionalFeatures, onSceneChanged);

    // Quella salvata nella scena è il punto di partenza; poi comanda l'hook, che sa se ne è appena
    // stata caricata una o se è stata tolta. `undefined` è la sola risposta che vuol dire «non
    // l'ho toccata»: `null` vuol dire che non c'è più, e deve vincere sul valore salvato.
    // L'AMBIENTE CHE C'È DAVVERO STA NELLA SCENA, non nel config: quello caricato dall'editor vive
    // nella serializzazione Babylon e non passa mai da `general.environmentTexture`, e dove non ce
    // n'è nessuno il motore ci mette il suo. Leggendo solo il config, una scena illuminata si
    // dichiarava senza illuminazione.
    const sceneEnvironmentUrl = typeof sceneEditor?.scene?.environmentTexture?.url === 'string' ? sceneEditor.scene.environmentTexture.url : null;
    const savedEnvironmentTexture = generalConfig?.get?.('environmentTexture') ?? sceneEnvironmentUrl;
    const environmentTexture = environment.environmentTexture === undefined ? savedEnvironmentTexture : environment.environmentTexture;

    // Quella di Zakeke non è una mappa come le altre: non si può togliere — il motore la rimette —
    // e chiamarla col nome del suo file direbbe a chi guarda che l'ha caricata lui.
    const isDefaultEnvironment = !!environmentTexture && environmentTexture.includes(DEFAULT_ENVIRONMENT_NAME);

    // IL FONDALE SUO CE L'HA SOLO CHI SE L'È CARICATO quando i due erano due cose separate, e si
    // legge dal config, non dallo stato dell'hook: quello nasce vuoto, e una scena che un fondale
    // ce l'ha davvero sembrava non averlo finché non se ne caricava un altro.
    const savedSkyboxTexture = skyboxConfig?.get?.('texture') ?? null;
    const skyboxTexture = environment.skyboxTexture === undefined ? savedSkyboxTexture : environment.skyboxTexture;

    // LE OPZIONI DELL'AMBIENTE SONO UN OGGETTO DEL CONFIG, e da lì si legge: è ciò che il
    // salvataggio manda al server. Su una scena che un ambiente non l'ha mai avuto quell'oggetto
    // non c'è ancora, e leggere non ha niente da dare: sono i valori di partenza. A scriverci ci
    // pensa il motore, che lo crea la prima volta che qualcuno gira una manopola.
    const environmentOptions = generalConfig?.get?.('environmentOptions');

    const [rotation, setRotation] = useState<number>(environmentOptions?.get?.('rotationY') ?? 0);
    const [environmentIntensity, setEnvironmentIntensity] = useState<number>(environmentOptions?.get?.('pbrIntensity') ?? 1);

    const [transparent, setTransparent] = useState<boolean>((generalConfig?.get?.('backgroundAlpha') ?? 1) === 0);
    const [backgroundColor, setBackgroundColor] = useState<string>(
        generalConfig?.get?.('backgroundColor') ?? environmentConfig?.get?.('backgroundColor') ?? '#EAECF0'
    );
    const [backdrop, setBackdrop] = useState<boolean>(skyboxConfig?.get?.('createSkybox') ?? false);
    const [backdropBlur, setBackdropBlur] = useState<number>(skyboxConfig?.get?.('blur') ?? 0);

    // LA ROTAZIONE SI SALVA, e prima no. `setSceneEnvironmentTextureRotationY` gira la texture in
    // Babylon e chiede un ridisegno, e basta: il config non lo tocca — a differenza di tutti i
    // `setViewer*` di questo pannello, che lo scrivono da sé. Ed è proprio dal config che questa
    // riga legge il suo valore aprendosi: si ruotava, si salvava, si ricaricava, e si tornava a
    // zero. Il difetto non si vedeva finché non si ricaricava.
    // E SI SALVA ANCHE SU UNA SCENA NUOVA. Le opzioni dell'ambiente possono non esistere ancora, e
    // scriverle con `?.` su un oggetto che non c'è non è un errore: è un nulla di fatto silenzioso.
    // `getOrCreateEnvironmentOptions` le crea al primo giro di manopola — come fa il modello con
    // ogni altro config.
    const changeRotation = useCallback(
        (value: number) => {
            setRotation(value);
            sceneEditor?.setSceneEnvironmentTextureRotationY?.(value);
            sceneEditor?.getOrCreateEnvironmentOptions?.()?.set?.('rotationY', value);
        },
        [sceneEditor]
    );

    // QUANTO L'AMBIENTE ILLUMINA. In un configuratore PBR la risposta è `pbrIntensity`: il motore,
    // caricando una scena, la prende di lì e la scrive su ogni PBRMaterial come
    // `environmentIntensity`. Quel giro non ha un setter pubblico che lo rifaccia a scena viva,
    // quindi qui si fa la stessa identica cosa: si scrive il config, che è ciò che si salva, e la
    // si applica ai materiali, che è ciò che si vede.
    const changeEnvironmentIntensity = useCallback(
        (value: number) => {
            setEnvironmentIntensity(value);
            sceneEditor?.getOrCreateEnvironmentOptions?.()?.set?.('pbrIntensity', value);

            (sceneEditor?.getAllMaterials?.() ?? []).forEach((material: any) => {
                if (typeof material?.environmentIntensity === 'number') material.environmentIntensity = value;
            });

            sceneEditor?.setRenderingNeeded?.();
        },
        [sceneEditor]
    );

    // I DUE FONDI SI ESCLUDONO, E SI SPENGONO A VICENDA. Uno sfondo o è il vuoto o è l'ambiente:
    // non esiste lo stato in cui sono tutti e due, e un interruttore disabilitato lo direbbe in
    // modo più debole — sembrerebbe rotto invece che superato.
    //
    // E L'ESCLUSIONE LA FA VALERE QUI, non il motore: là «fondo trasparente» non vuol dire
    // «nessuno sfondo», è anche la convenzione con cui la vetrina lascia vedere il colore del tema
    // dietro al canvas, e una regola nel motore toglierebbe il fondale proprio dove serve.
    const changeTransparent = useCallback(
        (value: boolean) => {
            if (value && backdrop) {
                setBackdrop(false);
                sceneEditor?.setViewerSkyboxCreateSkybox?.(false);
            }
            setTransparent(value);
            sceneEditor?.setViewerBackgroundAlpha?.(value ? 0 : 1);
        },
        [backdrop, sceneEditor]
    );

    const changeBackgroundColor = useCallback(
        (value: string) => {
            setBackgroundColor(value);
            sceneEditor?.setViewerBackgroundColor?.(value);
        },
        [sceneEditor]
    );

    const changeBackdrop = useCallback(
        (value: boolean) => {
            if (value && transparent) {
                setTransparent(false);
                sceneEditor?.setViewerBackgroundAlpha?.(1);
            }
            setBackdrop(value);
            sceneEditor?.setViewerSkyboxCreateSkybox?.(value);
        },
        [sceneEditor, transparent]
    );

    const changeBackdropBlur = useCallback(
        (value: number) => {
            setBackdropBlur(value);
            sceneEditor?.setViewerSkyboxBlur?.(value);
        },
        [sceneEditor]
    );

    // TOGLIENDO LA MAPPA, L'IMMAGINE SPARISCE DA TUTTI E DUE I MESTIERI. La luce no: una scena PBR
    // senza ambiente è spenta, e il motore ci rimette quella di Zakeke. Ma il fondale sì, perché
    // lasciarlo acceso vorrebbe dire mostrare come sfondo il softbox di Zakeke, che nessuno ha
    // scelto. Chi ha un fondale suo non viene toccato: quello resta, ed è lui a comandare.
    const removeEnvironment = useCallback(() => {
        environment.removeEnvironment();
        if (!skyboxTexture && backdrop) changeBackdrop(false);
    }, [backdrop, changeBackdrop, environment, skyboxTexture]);

    return (
        <InspectorShell kind='world' name={T._('World', 'Admin')} specs={T._('Environment · background', 'Admin')}>
            <PropertiesPanelSection title={T._('Ambience', 'Admin')}>
                <PropertiesPanelAccordion header={T._('Environment map', 'Admin')}>
                    {/* UN AMBIENTE SI GUARDA PRIMA DI REGOLARLO: è una panoramica, ed è la sola
                        anteprima di questo pannello che ha senso mostrare larga. Finché il dato che
                        abbiamo è una URL e basta, il riquadro porta il glifo — quando la miniatura
                        arriverà entra qui dentro, senza toccare nient'altro. */}
                    <PropertiesPanelResourceProperty
                        id='world-env-slot'
                        hero={<EnvironmentsIcon />}
                        preview={<EnvironmentsIcon />}
                        name={isDefaultEnvironment ? T._('Zakeke default environment', 'Admin') : fileNameOf(environmentTexture)}
                        caption={
                            isDefaultEnvironment
                                ? T._('Lights the scene until you load one', 'Admin')
                                : environmentTexture
                                  ? T._('Scene environment', 'Admin')
                                  : undefined
                        }
                        emptyLabel={T._('No environment map', 'Admin')}
                        onPick={environment.uploadEnvironment}
                        onReplace={environment.uploadEnvironment}
                        onClear={environmentTexture && !isDefaultEnvironment ? removeEnvironment : undefined}
                        disabled={environment.isEnvironmentUploading}
                    />
                    <Note>{T._('A .hdr, .dds or .env file, or the six cubemap faces named _px, _py, _pz, _nx, _ny, _nz.', 'Admin')}</Note>
                    {/* QUANTO ILLUMINA, prima di DA DOVE: è la domanda che si fa per prima davanti a
                        un ambiente nuovo, ed è la prima riga anche nel mockup. */}
                    <PropertiesPanelRangeProperty
                        id='world-env-intensity'
                        label={T._('Intensity', 'Admin')}
                        value={environmentIntensity}
                        min={0}
                        max={2}
                        step={0.05}
                        onChange={changeEnvironmentIntensity}
                    />
                    <PropertiesPanelRangeProperty
                        id='world-env-rotation'
                        label={T._('Rotation', 'Admin')}
                        value={rotation}
                        min={0}
                        max={360}
                        step={1}
                        unit='°'
                        onChange={changeRotation}
                    />
                </PropertiesPanelAccordion>

                {/* TRE MODI DI ESSERE UNO SFONDO, E UNO SOLO ALLA VOLTA: il vuoto, una tinta,
                    l'ambiente. I due interruttori si escludono spegnendosi a vicenda, e la tinta si
                    spegne da sé quando uno dei due copre tutto; «Blur» esiste solo per l'ambiente,
                    perché si sfuoca un'immagine, non una tinta. */}
                <PropertiesPanelAccordion header={T._('Background', 'Admin')}>
                    <PropertiesPanelSwitchProperty
                        id='world-bg-transparent'
                        label={T._('Transparent', 'Admin')}
                        checked={transparent}
                        onChange={changeTransparent}
                    />
                    {/* La tinta si spegne anche quando c'è un fondale, non solo col fondo
                        trasparente: davanti a un'immagine che copre tutto, un selettore di colore
                        che non cambia niente è un comando che mente. */}
                    <PropertiesPanelColorProperty
                        id='world-bg-color'
                        label={T._('Color', 'Admin')}
                        value={backgroundColor}
                        onChange={changeBackgroundColor}
                        disabled={transparent || backdrop}
                    />
                    {/* IL NOME DICE CHE COSA SI VEDRÀ, e cambia col caso: dove il fondale è
                        l'ambiente lo si chiama per nome, dove c'è un'immagine sua si parla di
                        quella. «Environment backdrop» del mockup non diceva né l'una né l'altra —
                        «backdrop» è una parola da teatro, non da chi vende scarpe. */}
                    <PropertiesPanelSwitchProperty
                        id='world-bg-backdrop'
                        label={skyboxTexture ? T._('Show the backdrop image', 'Admin') : T._('Show the environment as background', 'Admin')}
                        checked={backdrop}
                        onChange={changeBackdrop}
                    />
                    {/* UN'IMMAGINE SOLA, DUE MESTIERI: il fondale È la mappa d'ambiente, e non si
                        carica una seconda volta. Lo slot qui sotto non è un'offerta — compare solo
                        dove un fondale suo c'è già, caricato quando i due erano due cose separate.
                        Da quel mondo si esce, togliendolo, e non ci si rientra: è il concetto che
                        stiamo togliendo, non uno da proporre a chi arriva adesso. */}
                    {skyboxTexture ? (
                        <PropertiesPanelResourceProperty
                            id='world-skybox-slot'
                            preview={<EnvironmentsIcon />}
                            name={fileNameOf(skyboxTexture)}
                            caption={T._('This scene has its own backdrop image', 'Admin')}
                            onReplace={environment.uploadSkybox}
                            onClear={environment.removeSkybox}
                            disabled={environment.isSkyboxUploading || !backdrop}
                        />
                    ) : (
                        <Note>{T._('The backdrop shows the environment map.', 'Admin')}</Note>
                    )}
                    {/* Spento quando il fondale non c'è: sfocare uno sfondo che non si vede è un
                        comando che promette un effetto che non arriva. */}
                    <PropertiesPanelRangeProperty
                        id='world-bg-blur'
                        label={T._('Blur', 'Admin')}
                        value={backdropBlur}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={changeBackdropBlur}
                        disabled={!backdrop}
                    />
                </PropertiesPanelAccordion>
            </PropertiesPanelSection>

            {/* IL PIANO NON C'È PIÙ, E NON È UNA DIMENTICANZA (utente, 2026-09-16). I suoi otto
                comandi scrivevano in `environment.ground` del config, e quel ramo lo legge solo
                `buildEnvironmentHelperOptions`, dentro una funzione che nessuno chiama: colore,
                opacità, ombra, specchio e dimensione si salvavano e non li applicava nessuno — né
                qui né in vetrina. Quello che si vede col tasto Grid è un'altra cosa: la griglia
                dell'editor, che da quei valori non legge niente.
                Torna in round 2 come pavimento vero — un piano che riceve l'ombra del prodotto e
                può specchiarlo — quando nell'`EnvironmentManager` ci sarà chi lo costruisce. I
                valori già salvati restano nel JSON: togliere la sezione non cancella niente.
                Capitolo: `docs/scene-editor-ambiente-e-fondale.md`. */}
        </InspectorShell>
    );
};

export default WorldEditor;
